/**
 * Change your own password -- and the only page reachable while one is owed
 * (CLAUDE.md 19, 11.7).
 *
 * Uses `AuthLayout`, not `WorkspaceChrome`, deliberately. A user with
 * `password_change_required` is refused by `current_user`, so every workspace-scoped
 * call the normal chrome makes would 403. Rendering the application shell around the
 * one form that unblocks the account would show a page of failures instead.
 *
 * `POST /auth/password/change` revokes **every** session including this one, so there
 * is no signed-in state to return to: the user is signed out locally and sent to the
 * sign-in page. That is the same reasoning as the reset flow -- if the old password
 * was known to someone else, leaving a live session defeats the change.
 */
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ApiError, api } from '@/api/client'
import { useAuth } from '@/auth/AuthProvider'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Button } from '@/components/ui/button'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldInput } from '@/components/ui/input'
import { useToast } from '@/components/ui/toast'

const MIN_PASSWORD_LENGTH = 12

export function ChangePasswordPage() {
  const { t } = useTranslation(['auth', 'common'])
  const { user, logout } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()

  const [current, setCurrent] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const required = user?.password_change_required ?? false
  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH
  const mismatch = confirm.length > 0 && confirm !== password

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (tooShort || mismatch) return
    setPending(true)
    setError(null)
    try {
      await api.post('/auth/password/change', {
        current_password: current,
        new_password: password,
      })
      toast({ title: t('password.changed'), variant: 'success' })
      // Every refresh token was just revoked server-side, this one included.
      await logout()
      navigate('/login', { replace: true })
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.localised : String(caught))
    } finally {
      setPending(false)
    }
  }

  return (
    <AuthLayout title={t('password.change_title')}>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {required && (
          // Says *why* the rest of the application is out of reach. Without this the
          // redirect looks like a bug rather than a policy.
          <p
            role="status"
            className="rounded-md bg-[var(--status-warn-bg)] p-3 text-sm text-[var(--status-warn)]"
          >
            {t('password.change_required')}
          </p>
        )}

        {error && (
          <p
            role="alert"
            className="rounded-md bg-[var(--status-fail-bg)] p-3 text-sm text-[var(--status-fail)]"
          >
            {error}
          </p>
        )}

        <Field>
          <FieldLabel>{t('password.current')}</FieldLabel>
          <FieldInput
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
          />
        </Field>

        <Field
          description={t('register.password_hint')}
          error={
            tooShort ? t('error.auth.password_too_short', { minimum: MIN_PASSWORD_LENGTH }) : null
          }
        >
          <FieldLabel>{t('password.new')}</FieldLabel>
          <FieldInput
            type="password"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        <Field error={mismatch ? t('password.mismatch') : null}>
          <FieldLabel>{t('password.confirm')}</FieldLabel>
          <FieldInput
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
        </Field>

        <Button
          type="submit"
          variant="primary"
          className="w-full"
          loading={pending}
          disabled={tooShort || mismatch || !current || !password}
        >
          {t('password.change_title')}
        </Button>

        {/* Only when the change is voluntary -- a user who owes one has nowhere to go. */}
        {!required && (
          <Button asChild variant="secondary" className="w-full">
            <Link to="/settings">{t('common:action.cancel')}</Link>
          </Button>
        )}
      </form>
    </AuthLayout>
  )
}
